// @ts-nocheck

import * as Sentry from '@sentry/react';

import basketActionCreators from '../actions/basket';
import { cashoutRequest, cashoutSuccess, cashoutFail } from '../actions/cashout';
import flashMessageActionCreators from '../actions/flashMessages';
import { sendReport } from '../actions/report';
import storageActionCreators from '../actions/storage';
import userActionCreators from '../actions/user';
import Client from '../client';
import config from '../config/app.config';
import { CashoutErrorCodes } from '../constants/cashout';
import Formatter from '../i18n/formatter';
import { __, getCurrency, getLang } from '../i18n/l10n';
import { resetAllGRActives } from '../modules/gift-race/actions/basket';
import { selectEntitiesBets } from '../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesBetslipsEntity } from '../selectors/entities/betslips/entitiesBetslipsPrimary.selector';
import { selectEntitiesOutcomes } from '../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { CashoutActionTypes } from '../types/actions/cashout';
import { FlashMessageTypes, FlashMessageSeverities, FlashMessageScopes } from '../types/flashmessage';
import { getCustomHeaders } from '../utils';
import fetch from '../utils/fetch';

const { addMessage } = flashMessageActionCreators;

const interceptOn = [
  CashoutActionTypes.CASHOUT_TOTAL,
  CashoutActionTypes.CASHOUT_PARTIAL,
  CashoutActionTypes.CASHOUT_ENHANCE,
  CashoutActionTypes.CASHOUT_SUCCESS,
];

const dispatchMessage =
  (dispatch) =>
  (severity) =>
  ({ scope = FlashMessageScopes.BASKET, type = FlashMessageTypes.DISMISS_MANUAL, message }) => {
    dispatch(
      addMessage({
        scope,
        severity,
        type,
        message,
      }),
    );
  };

const submitCashout = async (payload, dispatch, getState) => {
  const { entities, user } = getState();
  const betslip = entities.betslips[payload.id];
  const { token: jwt } = user?.jwt ?? {};
  const { sessionId, cc } = user;

  const { ERROR, NOTICE } = FlashMessageSeverities;
  const dispatchFlashMessage = dispatchMessage(dispatch);
  const addNoticeMsg = dispatchFlashMessage(NOTICE);
  const addErrorMsg = dispatchFlashMessage(ERROR);

  // dispatch cashout in progress
  dispatch(cashoutRequest(payload.id));

  const { betslipReference } = betslip;
  const finalBetslipReference = betslipReference.replace(/^(\w)+-/, '');

  const data = {
    betslipId: payload.id,
    expectedValue: Math.round(payload.expectedValue * 100 * 100) / 100,
    cashoutValue: Math.round(payload.expectedValue * 100 * 100) / 100,
  };
  if (payload.cashoutAmount) {
    data.cashoutValue = Math.round(payload.cashoutAmount * 100 * 100) / 100;
  }
  const inlineBody = `cashout=${JSON.stringify(data)}`;
  const response = await fetch(config.cashoutUrl[getLang()], {
    method: 'POST',
    credentials: 'same-origin',
    body: inlineBody,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...getCustomHeaders({ jwt, sessionId, cc, tracker: Client.tracker }),
    },
  });
  let json = null;
  try {
    json = await response.json();
    if (json.ECODE === 0) {
      const amount = Formatter.formatNumber(Math.round((data.cashoutValue / 100) * 100) / 100, getCurrency());
      // dispatch cashout success
      dispatch(cashoutSuccess(payload.id));
      addNoticeMsg({ message: __('Your %1$s bet was cashed out for %2$s', [finalBetslipReference, amount]) });
    } else {
      const errorCode = json.ECODE;

      switch (errorCode) {
        case CashoutErrorCodes.USER_NOT_CONNECTED:
          if (getState().user.connected) {
            dispatch(sendReport({ message: 'validate_cashout failure', ECODE: errorCode }));

            Sentry.captureMessage('User sign-out from validate_cashout failure');
            dispatch(userActionCreators.setDisconnected());
          }
          break;
        case CashoutErrorCodes.CASHOUT_NOT_AVAILABLE:
          // prettier-ignore
          addErrorMsg({ message: __('Bet %1$s could not be redeemed, one or more selections are not eligible for Cashout', finalBetslipReference) });
          break;
        case CashoutErrorCodes.CASHOUT_VALUE_CHANGED:
          addErrorMsg({
            message: __('Bet %1$s could not be redeemed, the Cashout amount has changed', finalBetslipReference),
          });
          break;
        case CashoutErrorCodes.TOO_SMALL_RUNNING_AMOUNT:
          addErrorMsg({
            message: __('Bet %1$s could not be redeemed, remaining bet is less than €0.10', finalBetslipReference),
          });
          break;
        default:
          addErrorMsg({ message: __('Your %1$s bet could not be cashed out', finalBetslipReference) });
          break;
      }

      dispatch(cashoutFail(payload.id, json.estr));
    }
  } catch (error) {
    // dispatch cashout fail
    dispatch(cashoutFail(payload.id, error.message));
    addErrorMsg({ message: __('Your %1$s bet could not be cashed out', finalBetslipReference) });
  }
};

const enhanceBetslip = async (payload, dispatch, getState) => {
  const { id } = payload;
  console.debug(`%c cashout | enhance betslip ${id} `, 'background: #616161; color: #fff;');

  const state = getState();

  const betslip = selectEntitiesBetslipsEntity(state, { id });

  if (betslip) {
    const outcomes = selectEntitiesOutcomes(state);
    const bets = selectEntitiesBets(state);

    const items = betslip.details.map(({ outcomeId, odds }) => {
      const outcome = outcomes[outcomeId];
      const bet = (outcome && bets[outcome.betId]) || null;

      // Check if is a MyMatch or not
      if (outcome.hasOwnProperty('subOutcomes')) {
        const selectionItems = outcome.subOutcomes.map((subOutcome) =>
          Array.isArray(subOutcome) // is multichoice
            ? {
                matchId: bet.matchId,
                betId: outcomes[subOutcome[0]].betId,
                outcomeIds: subOutcome,
              }
            : {
                matchId: bet.matchId,
                betId: outcomes[subOutcome].betId,
                outcomeId: subOutcome,
              },
        );
        return {
          betbuilder: true,
          matchId: bet.matchId,
          betId: outcome.betId,
          outcomeId,
          odds: bet.isCancelled ? 1 : odds,
          selectionItems,
        };
      } else {
        return {
          matchId: bet.matchId,
          betId: outcome.betId,
          outcomeId,
          odds: bet.isCancelled ? 1 : odds,
        };
      }
    });

    dispatch(basketActionCreators.basketReset());
    dispatch(
      basketActionCreators.basketSetCashout({
        betAmount: `${betslip.betAmount}`,
        items,
        betslipType: betslip.type,
        betslipId: id,
        isFromFreebets: betslip.freebet ?? false,
      }),
    );
    dispatch(resetAllGRActives());
    dispatch(storageActionCreators.storageBasketSave);
  } else {
    console.debug(`%c cashout | unable to find betslip ${id} `, 'background: #b20e10; color: #fff;');
  }
};

export default ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    const isCashoutEnabled = getState().runtime.isCashoutEnabled;
    if (interceptOn.indexOf(action.type) !== -1) {
      const returnValue = next(action);
      if (action.type === CashoutActionTypes.CASHOUT_PARTIAL || action.type === CashoutActionTypes.CASHOUT_TOTAL) {
        if (isCashoutEnabled) submitCashout(action.payload, dispatch, getState);
      } else if (action.type === CashoutActionTypes.CASHOUT_ENHANCE) {
        if (isCashoutEnabled) enhanceBetslip(action.payload, dispatch, getState);
      } else if (action.type === CashoutActionTypes.CASHOUT_SUCCESS) {
        // if the cashouted betslip is in the betslip in mode cashout then clear the betslip
        const { basket } = getState();
        const { isCashout, cashoutBetslipId } = basket;
        if (isCashout && cashoutBetslipId === action.payload.id) {
          dispatch(basketActionCreators.basketClear());
        }
      }
      return returnValue;
    }
    return next(action);
  };
